import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { 
  Mail, Send, CheckCircle2, Loader2
} from 'lucide-react';
import { FaLinkedinIn, FaInstagram, FaTwitter } from 'react-icons/fa';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize EmailJS
  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey) {
      emailjs.init(publicKey);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (process.env.REACT_APP_EMAILJS_SERVICE_ID && process.env.REACT_APP_EMAILJS_TEMPLATE_ID) {
        await emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          {
            from_name: formData.name,
            from_email: formData.email,
            message: formData.message
          }
        );
      }

      if (process.env.REACT_APP_GOOGLE_SHEETS_URL) {
        try {
          fetch(process.env.REACT_APP_GOOGLE_SHEETS_URL, {
            method: 'POST',
            mode: 'no-cors',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              Timestamp: new Date().toISOString(),
              Name: formData.name,
              Email: formData.email,
              ProjectDetails: formData.message
            })
          }).catch(() => {});
        } catch (sheetError) {}
      }

      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', message: '' });
      }, 5000);
    } catch (err) {
      setError('Failed to send message. Please try again or email us directly.');
    } finally {
      setIsLoading(false);
    }
  };

  const socialLinks = [
    { icon: React.createElement(FaLinkedinIn as any, { size: 24 }), link: 'https://www.linkedin.com/company/svnate/', label: 'LinkedIn' },
    { icon: React.createElement(FaInstagram as any, { size: 24 }), link: 'https://www.instagram.com/svnate.official/', label: 'Instagram' },
    { icon: React.createElement(FaTwitter as any, { size: 24 }), link: 'https://x.com/svnate_official', label: 'Twitter' },
  ];

  return (
    <section className="bg-svnate-darkNavy py-24 px-6 md:px-12 relative" id="contact">
      <div className="container mx-auto relative z-10 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-16 text-center"
        >
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-white/10 bg-[#0f1419] ">
            <span className="text-svnate-lightTeal font-semibold text-sm">Contact Us</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
            Let's Talk
          </h2>
          <p className="text-lg md:text-xl text-foreground/70 max-w-2xl mx-auto">
            Tell us your idea — we'll help you choose the right solution. Get a free consultation and project estimate.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="bg-[#0f1419] border border-white/10 p-8 md:p-12 rounded-2xl "
          >
            <h3 className="text-3xl font-bold text-white mb-8">
              Send a Message
            </h3>
            
            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-16 text-center"
              >
                <CheckCircle2 className="w-20 h-20 text-svnate-teal mb-6" />
                <h4 className="text-3xl font-bold text-white mb-4">Message Sent!</h4>
                <p className="text-foreground/70">We'll respond within 24 hours.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-white/80 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-6 py-4 bg-[#0f1419] border border-white/10 rounded-xl focus:outline-none focus:border-svnate-teal transition-colors text-white placeholder-white/30"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-white/80 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-6 py-4 bg-[#0f1419] border border-white/10 rounded-xl focus:outline-none focus:border-svnate-teal transition-colors text-white placeholder-white/30"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-white/80 mb-2">
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-6 py-4 bg-[#0f1419] border border-white/10 rounded-xl focus:outline-none focus:border-svnate-teal transition-colors text-white placeholder-white/30 resize-none custom-scrollbar"
                    placeholder="Tell us about your project, goals, and timeline..."
                  ></textarea>
                </div>

                {error && (
                  <div className="text-red-400 text-sm font-medium bg-red-400/10 border border-red-400/20 p-4 rounded-xl">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-8 py-4 bg-white text-black rounded-xl font-bold flex items-center justify-center gap-3 hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send size={20} />
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="flex flex-col gap-6"
          >
            <div className="bg-[#0f1419] border border-white/10 p-8 rounded-2xl  hover:bg-white/10 transition-colors flex-1">
              <h3 className="text-2xl font-bold text-white mb-6">Direct Contact</h3>
              <a 
                href="mailto:support@svnate.com"
                className="flex items-center gap-4 text-white hover:text-svnate-lightTeal font-medium text-xl mb-4 transition-colors"
              >
                <div className="p-3 bg-[#0f1419] rounded-full">
                  <Mail className="w-6 h-6" />
                </div>
                support@svnate.com
              </a>
              <p className="text-foreground/70 leading-relaxed">
                Prefer email? Reach out directly and we'll respond within 24 hours.
              </p>
            </div>

            <div className="bg-[#0f1419] border border-white/10 p-8 rounded-2xl  hover:bg-white/10 transition-colors">
              <h3 className="text-xl font-bold text-white mb-6">Connect With Us</h3>
              <div className="flex gap-4">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0f1419] border border-white/10 text-white hover:bg-white/20 hover:text-svnate-lightTeal transition-all hover:scale-110"
                    aria-label={social.label}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            <div className="bg-[#0f1419] border border-white/10 p-8 rounded-2xl  hover:bg-white/10 transition-colors flex-1">
              <h3 className="text-xl font-bold text-white mb-6">What Happens Next?</h3>
              <ul className="space-y-6 text-foreground/70">
                <li className="flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-[#0f1419] border border-white/10 flex items-center justify-center text-white font-bold shrink-0">1</span>
                  <span>We'll review your project details and requirements.</span>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-[#0f1419] border border-white/10 flex items-center justify-center text-white font-bold shrink-0">2</span>
                  <span>Schedule a free 30-minute consultation call.</span>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-[#0f1419] border border-white/10 flex items-center justify-center text-white font-bold shrink-0">3</span>
                  <span>Receive a detailed proposal and timeline.</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
